import React, {useState, useEffect} from "react";
import { useNavigate } from "react-router-dom"
import { BsEyeFill, BsEyeSlashFill } from 'react-icons/bs'
import './UserPasswordReset.css'
import { getCurrentUser, isAuthenticated } from '../utils/auth';

export const ResetPasswordForm = () => {
    const navigate = useNavigate();
    const [existingPassword, setExistingPassword] = useState('');
    const [password, setPassword] = useState('');
    const [showExistingPassword, setShowExistingPassword] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [isResetting, setIsResetting] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);

    useEffect(() => {
        if (!isAuthenticated()) {
            navigate('/unauthorized');
        }
    }, [navigate]);

    const getCurrentUserContext = () => {
        const user = getCurrentUser();
        return {
            username: user?.username ?? window.sessionStorage.getItem('username') ?? ''
        };
    }

    const validateExistingPassword = async (username, existingPassword) => {
        const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/auth/login/', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                username: username,
                password: existingPassword
            })
        });

        if (!response.ok) {
            throw new Error('Existing password is incorrect.');
        }

        return true;
    }

    const extractUserId = (payload) => {
        if (!payload) return null;

        if (Array.isArray(payload)) {
            for (const item of payload) {
                const foundId = extractUserId(item);
                if (foundId) return foundId;
            }
            return null;
        }

        if (typeof payload !== 'object') return null;

        const directId = payload.user_id ?? payload.id ?? payload.web_user_id ?? payload.userId ?? null;
        if (directId) return directId;

        const nestedKeys = ['user', 'data', 'result', 'results', 'users', 'items'];
        for (const key of nestedKeys) {
            if (payload[key] !== undefined && payload[key] !== null) {
                const nestedId = extractUserId(payload[key]);
                if (nestedId) return nestedId;
            }
        }

        return null;
    }

    const getUserIdByUsername = async (username) => {
        const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/users/username/' + encodeURIComponent(username), {
            method: 'GET',
            headers: {
                'machine':'2',
                'vh-key':'7df7f81e-aaff-444c-a01c-8caeaeab3e61',
            },
        });

        if (!response.ok) {
            throw new Error('Unable to load user id from username.');
        }

        let responseBody = {};
        try {
            responseBody = await response.json();
        } catch (jsonError) {
            responseBody = {};
        }

        const userId = extractUserId(responseBody);

        if (!userId) {
            throw new Error('Unable to determine user id from username response.');
        }

        return userId;
    }

    const patchNewPassword = async (userId, password) => {
        const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/users/user/' + userId,
            {
                method: 'PATCH',
                headers: {
                    'machine':'2',
                    'vh-key':'7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                    'content-type': 'application/json'
                },
                body: JSON.stringify({
                    password: password
                }),
            });

        if (!response.ok) {
            throw new Error(`Failed to reset password. Status: ${response.status}`);
        }

        return true;
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!existingPassword.trim() || !password.trim()) {
            setError('All password fields are required.');
            return;
        }

        const { username } = getCurrentUserContext();
        if (!username) {
            setError('Unable to identify current user username.');
            return;
        }

        setIsResetting(true);
        try {
            await validateExistingPassword(username, existingPassword);
            const userId = await getUserIdByUsername(username);
            await patchNewPassword(userId, password);
            setExistingPassword('');
            setPassword('');
            setShowSuccessDialog(true);
        } catch (requestError) {
            console.error(requestError);
            setError(requestError.message || 'Failed to reset password. Please try again.');
        } finally {
            setIsResetting(false);
        }
    }

    return (
        <div className='user-password-reset-page'>
            <div className='user-password-reset-card'>
                <h2>Reset Password</h2>
                <p className='user-password-reset-subtitle'>Enter your existing password to validate, then set a new password.</p>

                <form onSubmit={handleSubmit}>
                    <label className='user-password-reset-label' htmlFor='existing-password-input'>Existing Password</label>
                    <div className='user-password-input-wrap'>
                        <input
                            id='existing-password-input'
                            className='user-password-reset-input'
                            type={showExistingPassword ? 'text' : 'password'}
                            value={existingPassword}
                            onChange={(event) => setExistingPassword(event.target.value)}
                            placeholder='Enter existing password'
                            autoComplete='current-password'
                        />
                        <button
                            type='button'
                            className='user-password-visibility-btn'
                            onClick={() => setShowExistingPassword((previous) => !previous)}
                            aria-label={showExistingPassword ? 'Hide existing password' : 'Show existing password'}
                        >
                            {showExistingPassword ? <BsEyeSlashFill /> : <BsEyeFill />}
                        </button>
                    </div>

                    <label className='user-password-reset-label' htmlFor='new-password-input'>Password</label>
                    <div className='user-password-input-wrap'>
                        <input
                            id='new-password-input'
                            className='user-password-reset-input'
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(event) => setPassword(event.target.value)}
                            placeholder='Enter password'
                            autoComplete='new-password'
                        />
                        <button
                            type='button'
                            className='user-password-visibility-btn'
                            onClick={() => setShowPassword((previous) => !previous)}
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                            {showPassword ? <BsEyeSlashFill /> : <BsEyeFill />}
                        </button>
                    </div>

                    {error && <div className='error user-password-reset-error'>{error}</div>}

                    <div className='user-password-reset-actions'>
                        <button type='button' className='btn btn-cancel' onClick={() => navigate('/users', { replace: true })} disabled={isResetting}>Cancel</button>
                        <button type='submit' className='btn' disabled={isResetting}>
                            {isResetting ? 'Resetting Password...' : 'Reset Password'}
                        </button>
                    </div>
                </form>
            </div>

            {showSuccessDialog && (
                <div className='modal-container'>
                    <div className='modal-edit user-password-success-modal'>
                        <p className='user-password-success-message'>Password successfully reset.</p>
                        <div className='user-password-success-actions'>
                            <button type='button' className='btn' onClick={() => navigate('/')}>OK</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ResetPasswordForm;